import React, { useState, useEffect } from 'react';
import { X, FileText, Download, AlertCircle, Loader } from 'lucide-react';

const BolViewModal = ({ isOpen, onClose, truckId, truckInfo }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && truckId) {
      loadPdf();
    }

    // Cleanup function to revoke object URL
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen, truckId]);

  const loadPdf = async () => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/outbound-trucks/${truckId}/view-bol`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load BOL file');
      }

      const blob = await response.blob();
      
      // Check if it's a PDF
      const contentType = response.headers.get('content-type') || blob.type;
      console.log('BOL file content type:', contentType);
      
      // Create blob URL with proper mime type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Error loading BOL:', err);
      setError('Failed to load BOL file. Please try downloading instead.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const token = localStorage.getItem('token');
    const bolFileName = truckInfo?.bol_file_path?.split('/').pop() || `BOL-${truckId}.pdf`;
    
    fetch(`/api/outbound-trucks/${truckId}/download-bol`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to download BOL');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = bolFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error('Error downloading BOL:', error);
      alert('Failed to download BOL file');
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-6xl h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-200">View Bill of Lading</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button 
              className="text-slate-400 hover:text-slate-200"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Truck info */}
        {truckInfo && (
          <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-700">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-400">Carrier:</span>
              <span className="text-slate-200 font-medium">{truckInfo.carrier}</span>
              {truckInfo.customer && (
                <>
                  <span className="text-slate-400">Customer:</span>
                  <span className="text-slate-200">{truckInfo.customer}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden bg-slate-900">
          {isLoading ? (
            <div className="h-full flex flex-col items-center justify-center">
              <Loader className="w-8 h-8 text-blue-500 animate-spin mb-4" />
              <p className="text-slate-400">Loading BOL file...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center">
              <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-red-200 mb-4">{error}</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Instead
              </button>
            </div>
          ) : (
            <embed
              src={pdfUrl}
              type="application/pdf"
              className="w-full h-full"
              title="BOL PDF Viewer"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BolViewModal;